// File: src/i18n/strings.js
/**
 * UI chrome strings for the three interface languages (en / es / ar).
 *
 * These cover the app scaffolding — navigation, home page, sidebar, search,
 * section metadata, footer — so Spanish and Arabic speakers can navigate the
 * app, not just read translated regulation text. Regulation CONTENT comes from
 * the osha-es-kit pipeline, never from here.
 *
 * Arabic values are Modern Standard Arabic and render RTL inside their own
 * elements (dir set per-element where it matters); the shell layout stays LTR.
 */

export const UI_LANGS = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'ar', label: 'العربية', short: 'ع' },
];

const STRINGS = {
  en: {
    // Header / navigation
    'nav.home': 'Home',
    'nav.search': 'Search',
    'nav.login': 'Login',
    'nav.clientLogin': 'Client Login',
    'nav.visitCdrisk': 'Visit CDRisk.com',
    'nav.toggleSidebar': 'Toggle sidebar',
    'nav.goBack': 'Go back',
    'nav.language': 'Language',
    'title.search': 'Search Regulations',

    // Home view
    'home.title': 'OSHA Regulation Viewer',
    'home.tagline': 'Access comprehensive OSHA safety regulations for your workplace compliance needs.',

    // Sidebar
    'sidebar.loading': 'Loading...',
    'sidebar.noTopics': 'No topics available',
    'sidebar.loadError': 'Failed to load regulation data',
    'sidebar.byTopic': 'By Topic',
    'sidebar.byCode': 'By Code',

    // Search
    'search.placeholder': 'Search regulations...',
    'search.recent': 'Recent Searches',
    'search.clear': 'Clear search',
    'search.searching': 'Searching...',
    'search.enterTerm': 'Enter a search term to find regulations',
    'search.hint': 'Try searching for "fall protection" or "1926.501"',
    'search.noResults': 'No results found for "{q}"',
    'search.noResultsHint': 'Try different keywords or check spelling',
    'search.resultsFor': '{n} results for "{q}"',
    'search.resultFor': '1 result for "{q}"',
    'search.sortBy': 'Sort by:',
    'search.sort.relevance': 'Relevance',
    'search.sort.date': 'Date Updated',
    'search.sort.code': 'Code Number',
    'search.filter.regulation': 'Regulation:',
    'search.filter.all': 'All',
    'search.subpart': 'Subpart {s}',
    'search.updated': 'Updated {d}',
    'search.critical': 'Critical',
    'search.failed': 'Search failed. Please try again.',

    // Section viewer
    'section.loading': 'Loading section...',
    'section.loadErrorTitle': 'Unable to Load Section',
    'section.none': 'No section selected',
    'section.noContent': 'No content available',
    'section.codeRevised': 'Code last revised: {d}',
    'section.sourcePulled': 'Source pulled: {d}',
    'section.translationsUpdated': 'Translations updated: {d}',
    'section.translationsPending': 'Translations: pending',
    'section.minRead': '{n} min read',
    'section.critical': 'Critical Safety',
    'section.popular': 'Popular',
    'section.related': 'Related Sections',

    // Bottom action bar
    'actions.save': 'Save',
    'actions.saved': 'Saved',
    'actions.share': 'Share',
    'actions.pdf': 'PDF',
    'actions.note': 'Note',

    // Footer
    'footer.description': 'Expert consultation in occupational health & safety, fire safety, hazardous materials management, and regulatory compliance.',
    'footer.contact': 'Contact Us',
    'footer.quickLinks': 'Quick Links',
    'footer.resources': 'Resources',
    'footer.oshaRegs': 'OSHA Regulations',
    'footer.clientPortal': 'Client Portal',
    'footer.consultation': 'Schedule Consultation',
    'footer.fireSafety': 'Fire Safety',
    'footer.compliance': 'Compliance Services',
    'footer.sdsGuide': 'SDS.guide — Safety Data Sheets',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
  },

  es: {
    'nav.home': 'Inicio',
    'nav.search': 'Buscar',
    'nav.login': 'Ingresar',
    'nav.clientLogin': 'Acceso de clientes',
    'nav.visitCdrisk': 'Visitar CDRisk.com',
    'nav.toggleSidebar': 'Abrir/cerrar menú lateral',
    'nav.goBack': 'Volver',
    'nav.language': 'Idioma',
    'title.search': 'Buscar regulaciones',

    'home.title': 'Visor de Regulaciones de OSHA',
    'home.tagline': 'Acceda a las regulaciones de seguridad de OSHA para el cumplimiento normativo de su lugar de trabajo.',

    'sidebar.loading': 'Cargando...',
    'sidebar.noTopics': 'No hay temas disponibles',
    'sidebar.loadError': 'No se pudieron cargar los datos de la regulación',
    'sidebar.byTopic': 'Por tema',
    'sidebar.byCode': 'Por código',

    'search.placeholder': 'Buscar regulaciones...',
    'search.recent': 'Búsquedas recientes',
    'search.clear': 'Borrar búsqueda',
    'search.searching': 'Buscando...',
    'search.enterTerm': 'Escriba un término para buscar regulaciones',
    'search.hint': 'Pruebe con "protección contra caídas" o "1926.501"',
    'search.noResults': 'No se encontraron resultados para "{q}"',
    'search.noResultsHint': 'Pruebe con otras palabras o revise la ortografía',
    'search.resultsFor': '{n} resultados para "{q}"',
    'search.resultFor': '1 resultado para "{q}"',
    'search.sortBy': 'Ordenar por:',
    'search.sort.relevance': 'Relevancia',
    'search.sort.date': 'Fecha de actualización',
    'search.sort.code': 'Número de código',
    'search.filter.regulation': 'Regulación:',
    'search.filter.all': 'Todas',
    'search.subpart': 'Subparte {s}',
    'search.updated': 'Actualizado {d}',
    'search.critical': 'Crítico',
    'search.failed': 'La búsqueda falló. Inténtelo de nuevo.',

    'section.loading': 'Cargando sección...',
    'section.loadErrorTitle': 'No se pudo cargar la sección',
    'section.none': 'Ninguna sección seleccionada',
    'section.noContent': 'Contenido no disponible',
    'section.codeRevised': 'Última revisión del código: {d}',
    'section.sourcePulled': 'Fuente sincronizada: {d}',
    'section.translationsUpdated': 'Traducciones actualizadas: {d}',
    'section.translationsPending': 'Traducciones: pendientes',
    'section.minRead': '{n} min de lectura',
    'section.critical': 'Seguridad crítica',
    'section.popular': 'Popular',
    'section.related': 'Secciones relacionadas',

    'actions.save': 'Guardar',
    'actions.saved': 'Guardado',
    'actions.share': 'Compartir',
    'actions.pdf': 'PDF',
    'actions.note': 'Nota',

    'footer.description': 'Consultoría experta en salud y seguridad ocupacional, seguridad contra incendios, manejo de materiales peligrosos y cumplimiento normativo.',
    'footer.contact': 'Contáctenos',
    'footer.quickLinks': 'Enlaces rápidos',
    'footer.resources': 'Recursos',
    'footer.oshaRegs': 'Regulaciones de OSHA',
    'footer.clientPortal': 'Portal de clientes',
    'footer.consultation': 'Agendar consulta',
    'footer.fireSafety': 'Seguridad contra incendios',
    'footer.compliance': 'Servicios de cumplimiento',
    'footer.sdsGuide': 'SDS.guide — Hojas de datos de seguridad',
    'footer.rights': 'Todos los derechos reservados.',
    'footer.privacy': 'Política de privacidad',
    'footer.terms': 'Términos de servicio',
  },

  ar: {
    'nav.home': 'الرئيسية',
    'nav.search': 'بحث',
    'nav.login': 'تسجيل الدخول',
    'nav.clientLogin': 'دخول العملاء',
    'nav.visitCdrisk': 'زيارة CDRisk.com',
    'nav.toggleSidebar': 'فتح/إغلاق القائمة الجانبية',
    'nav.goBack': 'رجوع',
    'nav.language': 'اللغة',
    'title.search': 'البحث في اللوائح',

    'home.title': 'عارض لوائح OSHA',
    'home.tagline': 'اطّلع على لوائح السلامة الصادرة عن OSHA لتلبية متطلبات الامتثال في مكان عملك.',

    'sidebar.loading': 'جارٍ التحميل...',
    'sidebar.noTopics': 'لا توجد مواضيع متاحة',
    'sidebar.loadError': 'تعذّر تحميل بيانات اللائحة',
    'sidebar.byTopic': 'حسب الموضوع',
    'sidebar.byCode': 'حسب الرقم',

    'search.placeholder': 'ابحث في اللوائح...',
    'search.recent': 'عمليات البحث الأخيرة',
    'search.clear': 'مسح البحث',
    'search.searching': 'جارٍ البحث...',
    'search.enterTerm': 'أدخل كلمة للبحث في اللوائح',
    'search.hint': 'جرّب البحث عن "fall protection" أو "1926.501"',
    'search.noResults': 'لا توجد نتائج لـ "{q}"',
    'search.noResultsHint': 'جرّب كلمات أخرى أو تحقّق من الإملاء',
    'search.resultsFor': '{n} نتيجة لـ "{q}"',
    'search.resultFor': 'نتيجة واحدة لـ "{q}"',
    'search.sortBy': 'ترتيب حسب:',
    'search.sort.relevance': 'الصلة',
    'search.sort.date': 'تاريخ التحديث',
    'search.sort.code': 'رقم المادة',
    'search.filter.regulation': 'اللائحة:',
    'search.filter.all': 'الكل',
    'search.subpart': 'الجزء الفرعي {s}',
    'search.updated': 'حُدِّث {d}',
    'search.critical': 'حرج',
    'search.failed': 'فشل البحث. حاول مرة أخرى.',

    'section.loading': 'جارٍ تحميل المادة...',
    'section.loadErrorTitle': 'تعذّر تحميل المادة',
    'section.none': 'لم يتم اختيار مادة',
    'section.noContent': 'المحتوى غير متوفر',
    'section.codeRevised': 'آخر مراجعة للنص: {d}',
    'section.sourcePulled': 'آخر سحب للمصدر: {d}',
    'section.translationsUpdated': 'آخر تحديث للترجمات: {d}',
    'section.translationsPending': 'الترجمات: قيد الإعداد',
    'section.minRead': '{n} دقيقة قراءة',
    'section.critical': 'سلامة حرجة',
    'section.popular': 'شائع',
    'section.related': 'مواد ذات صلة',

    'actions.save': 'حفظ',
    'actions.saved': 'محفوظ',
    'actions.share': 'مشاركة',
    'actions.pdf': 'PDF',
    'actions.note': 'ملاحظة',

    'footer.description': 'استشارات متخصصة في الصحة والسلامة المهنية، والسلامة من الحرائق، وإدارة المواد الخطرة، والامتثال التنظيمي.',
    'footer.contact': 'اتصل بنا',
    'footer.quickLinks': 'روابط سريعة',
    'footer.resources': 'موارد',
    'footer.oshaRegs': 'لوائح OSHA',
    'footer.clientPortal': 'بوابة العملاء',
    'footer.consultation': 'حجز استشارة',
    'footer.fireSafety': 'السلامة من الحرائق',
    'footer.compliance': 'خدمات الامتثال',
    'footer.sdsGuide': 'SDS.guide — صحائف بيانات السلامة',
    'footer.rights': 'جميع الحقوق محفوظة.',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.terms': 'شروط الخدمة',
  },
};

// Localized homepage regulation-card copy (part titles + category badges).
// English part titles are the official CFR names; ES/AR give a faithful gloss.
export const REGULATION_CARDS = {
  en: {
    1910: { desc: 'Occupational Safety and Health Standards', badge: 'General Industry' },
    1926: { desc: 'Safety and Health Regulations for Construction', badge: 'Construction' },
    1902: { desc: 'State Plans for the Development and Enforcement of Standards', badge: 'State Plans' },
    1903: { desc: 'Inspections, Citations, and Proposed Penalties', badge: 'Enforcement' },
    1904: { desc: 'Recording and Reporting Occupational Injuries and Illnesses', badge: 'Recordkeeping' },
    1905: { desc: 'Rules of Practice for Variances, Tolerances, and Exemptions', badge: 'Variances' },
    1915: { desc: 'Occupational Safety and Health Standards for Shipyard Employment', badge: 'Maritime' },
    1917: { desc: 'Marine Terminals', badge: 'Maritime' },
    1918: { desc: 'Safety and Health Regulations for Longshoring', badge: 'Maritime' },
    1919: { desc: 'Gear Certification', badge: 'Maritime' },
    1928: { desc: 'Occupational Safety and Health Standards for Agriculture', badge: 'Agriculture' },
  },
  es: {
    1910: { desc: 'Normas de seguridad y salud ocupacional', badge: 'Industria general' },
    1926: { desc: 'Regulaciones de seguridad y salud para la construcción', badge: 'Construcción' },
    1902: { desc: 'Planes estatales para el desarrollo y la aplicación de normas', badge: 'Planes estatales' },
    1903: { desc: 'Inspecciones, citaciones y sanciones propuestas', badge: 'Aplicación' },
    1904: { desc: 'Registro y notificación de lesiones y enfermedades ocupacionales', badge: 'Registros' },
    1905: { desc: 'Reglas de procedimiento para variancias, tolerancias y exenciones', badge: 'Variancias' },
    1915: { desc: 'Normas de seguridad y salud para el trabajo en astilleros', badge: 'Marítimo' },
    1917: { desc: 'Terminales marítimas', badge: 'Marítimo' },
    1918: { desc: 'Regulaciones de seguridad y salud para trabajos de estiba', badge: 'Marítimo' },
    1919: { desc: 'Certificación de equipos de izado', badge: 'Marítimo' },
    1928: { desc: 'Normas de seguridad y salud ocupacional para la agricultura', badge: 'Agricultura' },
  },
  ar: {
    1910: { desc: 'معايير السلامة والصحة المهنية', badge: 'الصناعة العامة' },
    1926: { desc: 'لوائح السلامة والصحة لأعمال البناء', badge: 'البناء' },
    1902: { desc: 'خطط الولايات لوضع المعايير وإنفاذها', badge: 'خطط الولايات' },
    1903: { desc: 'عمليات التفتيش والمخالفات والغرامات المقترحة', badge: 'الإنفاذ' },
    1904: { desc: 'تسجيل الإصابات والأمراض المهنية والإبلاغ عنها', badge: 'حفظ السجلات' },
    1905: { desc: 'قواعد الإجراءات للاستثناءات والتفاوتات والإعفاءات', badge: 'الاستثناءات' },
    1915: { desc: 'معايير السلامة والصحة المهنية للعمل في أحواض بناء السفن', badge: 'بحري' },
    1917: { desc: 'المحطات البحرية', badge: 'بحري' },
    1918: { desc: 'لوائح السلامة والصحة لأعمال الشحن والتفريغ', badge: 'بحري' },
    1919: { desc: 'اعتماد معدات الرفع', badge: 'بحري' },
    1928: { desc: 'معايير السلامة والصحة المهنية للزراعة', badge: 'الزراعة' },
  },
};

/**
 * Look up a UI string for a language, with {token} interpolation.
 * Falls back to English, then to the key itself, so a missing string can
 * never blank out the UI.
 */
export function translate(lang, key, params = {}) {
  const table = STRINGS[lang] || STRINGS.en;
  let str = table[key] != null ? table[key] : STRINGS.en[key];
  if (str == null) return key;
  for (const [name, value] of Object.entries(params)) {
    str = str.split(`{${name}}`).join(String(value));
  }
  return str;
}

export default STRINGS;
